import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { builtInFonts } from 'lib/reader';
import { Button } from 'components/common';
import { Box } from 'components/common/box';
import {
  Checkbox,
  Form,
  FormGroup,
  NumberStepper,
  Select,
  Text
} from 'components/common/form';

const SiteSettingsReader = () => {
  const { dispatch } = useDispatch();

  const { messages, serverConfig } = useReduxState();

  const [saving, setSaving] = useState(false);

  const saveSettings = async (values: typeof initialValues) => {
    try {
      setSaving(true);

      await api.put(`/admin/server-config`, {
        data: values
      });

      const response = await api.get('/admin/server-config');
      dispatch(actions.setServerConfig(response.data.data));
      dispatch(
        actions.addNotice({
          text: messages.settingsSavedSuccessfully,
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setSaving(false);
    }
  };

  const initialValues = {
    readerBuiltInFonts: serverConfig.readerBuiltInFonts,
    readerDefaultFont: serverConfig.readerDefaultFont,
    readerDefaultChapterWidth: serverConfig.readerDefaultChapterWidth,
    readerEnableChapterAnswersDownload:
      serverConfig.readerEnableChapterAnswersDownload,
    readerEnableChat: serverConfig.readerEnableChat,
    readerEnableChatAnonymousComments:
      serverConfig.readerEnableChatAnonymousComments,
    readerEnableFeedback: serverConfig.readerEnableFeedback,
    readerEnablePagesView: serverConfig.readerEnablePagesView,
    readerEnableSearch: serverConfig.readerEnableSearch,
    readerEnableSearchGoogle: serverConfig.readerEnableSearchGoogle,
    readerEnableSearchLocal: serverConfig.readerEnableSearchLocal,
    readerEnableSearchTranslate: serverConfig.readerEnableSearchTranslate,
    readerEnableSearchWikipedia: serverConfig.readerEnableSearchWikipedia
  };

  return (
    <div className="settings">
      <Box title={messages.siteReaderSettings}>
        <Form
          allowEmptyStrings={true}
          initialValues={initialValues}
          onSubmit={saveSettings}
        >
          {({ formState }) => (
            <div>
              <div className="row">
                <div className="col">
                  <FormGroup label={messages.siteReaderBuiltInFonts}>
                    {builtInFonts.map((font) => (
                      <Checkbox
                        key={font.id}
                        field={`readerBuiltInFonts.${font.id}`}
                        id={`reader-built-in-fonts-${font.id}`}
                        label={font.label}
                        style={{ marginBottom: 5 }}
                      />
                    ))}
                  </FormGroup>
                </div>
              </div>

              <FormGroup label={messages.siteReaderDefaultFont}>
                <Select
                  field="readerDefaultFont"
                  id={`reader-default-font`}
                  options={[...builtInFonts]}
                  style={{ width: 300, maxWidth: '100%' }}
                />
              </FormGroup>

              <FormGroup label={messages.commonChapter}>
                <NumberStepper
                  field="readerDefaultChapterWidth"
                  id="reader-default-chapter-width"
                  label={messages.siteReaderDefaultChapterWidth}
                  min={300}
                  max={1920}
                  stepValue={20}
                />
              </FormGroup>

              <FormGroup label={messages.commonChat}>
                <Checkbox
                  field="readerEnableChat"
                  id="reader-enable-chat"
                  label={messages.settingsEnableChat}
                />
                <Checkbox
                  disabled={!formState.values.readerEnableChat}
                  field="readerEnableChatAnonymousComments"
                  id="reader-enable-chat-anonymous-comments"
                  label={messages.settingsEnableChatAnonymousComments}
                  style={{ marginTop: 5 }}
                />
              </FormGroup>

              <FormGroup label={messages.commonFeedback}>
                <Checkbox
                  field="readerEnableFeedback"
                  id="reader-enable-feedback"
                  label={messages.siteReaderEnableFeedback}
                />
              </FormGroup>

              <FormGroup label={messages.readerHeaderSearch}>
                <Checkbox
                  field="readerEnableSearch"
                  id="reader-enable-search"
                  label={messages.siteReaderEnableSearch}
                />
                <Checkbox
                  field="readerEnableSearchLocal"
                  id="reader-enable-search-local"
                  label={messages.siteReaderEnableSearchLocal}
                  style={{ marginTop: 5 }}
                />
                <Checkbox
                  field="readerEnableSearchWikipedia"
                  id="reader-enable-search-wikipedia"
                  label={messages.siteReaderEnableSearchWikipedia}
                  style={{ marginTop: 5 }}
                />
                <Checkbox
                  field="readerEnableSearchTranslate"
                  id="reader-enable-search-translate"
                  label={messages.siteReaderEnableSearchTranslate}
                  style={{ marginTop: 5 }}
                />
                <Checkbox
                  field="readerEnableSearchGoogle"
                  id="reader-enable-search-google"
                  label={messages.siteReaderEnableSearchGoogle}
                  style={{ marginTop: 5 }}
                />
              </FormGroup>

              <FormGroup label={messages.tasks}>
                <Checkbox
                  field="readerEnableChapterAnswersDownload"
                  id="reader-enable-chapter-answers-download"
                  label={messages.siteReaderEnableChapterAnswersDownload}
                />
              </FormGroup>

              <FormGroup label={messages.commonView}>
                <Checkbox
                  field="readerEnablePagesView"
                  id="reader-enable-pages-view"
                  label={messages.siteReaderEnablePagesView}
                />
              </FormGroup>

              <FormGroup>
                <Button
                  category="success"
                  loading={saving}
                  text={messages.commonSave}
                  type="submit"
                />
              </FormGroup>
            </div>
          )}
        </Form>
      </Box>
    </div>
  );
};

export default SiteSettingsReader;
